import axios from 'axios';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import range from 'lodash/range';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
export function handleRequestErrors(error) {
    console.log(error);
    if (error?.response?.status === 424) {
        return alert('Failed to generate Quote PDF');
    }
    if (error?.response?.status === 400) {
        return alert(error?.response?.data || 'Invalid request');
    }
    alert('Something went terribly wrong');
}
export function SearchControl({ defaultPlaceholder, value, handleSearchChange }) {
    const handleChange = (e) => {
        handleSearchChange(e.target.value);
    };
    const clear = () => {
        const value = '';
        handleSearchChange(value, true);
    };
    const placeholder = defaultPlaceholder || 'Search';
    return (React.createElement("div", { className: "form-floating mb-3 search-input" },
        React.createElement("input", { type: "text", className: "form-control", id: "search-control", placeholder: placeholder, value: value, onChange: handleChange }),
        React.createElement("label", { htmlFor: "search-control" }, placeholder),
        value && (React.createElement("span", { className: "clear bg-secondary", onClick: clear }))));
}
export function EntriesCounter({ currentPage, perPage, totalEntries, isSearch }) {
    let currentPageMax = currentPage * perPage;
    if (currentPageMax > totalEntries)
        currentPageMax = totalEntries;
    const currentPageMin = ((currentPage * perPage) - perPage) + 1;
    let prefix = 'Showing';
    if (isSearch) {
        prefix = 'Found';
    }
    if (totalEntries <= perPage) {
        return (React.createElement(React.Fragment, null,
            prefix,
            " ",
            totalEntries,
            " entries"));
    }
    return (React.createElement(React.Fragment, null,
        prefix,
        " ",
        currentPageMin,
        " to ",
        currentPageMax,
        " of ",
        totalEntries,
        " entries"));
}
export function Pager({ usrHalfWindowSize, changePage, page, pages, display }) {
    let halfWindowSize;
    if (typeof usrHalfWindowSize !== 'number') {
        halfWindowSize = parseInt(usrHalfWindowSize, 10);
    }
    if (isNaN(halfWindowSize) || !isNumber(halfWindowSize)) {
        halfWindowSize = 2;
    }
    const fullWindowSize = halfWindowSize * 2;
    const changePageWrapper = (num) => {
        return ((e) => {
            e.preventDefault();
            changePage(num);
        });
    };
    const getPagesWindow = () => {
        let start = page - halfWindowSize, end = page + halfWindowSize;
        if (start <= 1) {
            start = 1;
            end = start + fullWindowSize;
        }
        if (end >= pages) {
            start = pages - fullWindowSize;
            end = pages;
        }
        if (start < 1)
            start = 1;
        if (end < 1)
            end = 1;
        return range(start, end + 1);
    };
    if (!display)
        return null;
    if (pages === 1)
        return null;
    const pagesRange = getPagesWindow();
    return (React.createElement("nav", { "aria-label": "Page navigation" },
        React.createElement("ul", { className: "pagination justify-content-end" },
            !isEmpty(pagesRange) && !pagesRange.includes(1) && (React.createElement("li", { className: "page-item" },
                React.createElement("a", { className: "page-link", onClick: changePageWrapper(1), tabIndex: -1 }, "First"))),
            page > 1 && (React.createElement("li", { className: "page-item" },
                React.createElement("a", { className: "page-link", onClick: changePageWrapper(page - 1), tabIndex: -1 }, "Prev"))),
            pagesRange.map((num) => {
                return (React.createElement("li", { className: `page-item ${num === page && 'active'}`, key: `page-${num}` },
                    React.createElement("a", { className: "page-link", onClick: changePageWrapper(num) }, num)));
            }),
            page < pages && (React.createElement("li", { className: "page-item" },
                React.createElement("a", { className: "page-link", onClick: changePageWrapper(page + 1) }, "Next"))),
            !isEmpty(pagesRange) && !pagesRange.includes(pages) && (React.createElement("li", { className: "page-item" },
                React.createElement("a", { className: "page-link", onClick: changePageWrapper(pages) }, "Last"))))));
}
export function ListingFooter({ isSearch, currentPage, perPage, totalPages, totalEntries, changePage }) {
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "row pt-3" },
            React.createElement("div", { className: "col-md d-none d-sm-none d-md-block" },
                React.createElement(EntriesCounter, { isSearch: isSearch, currentPage: currentPage, perPage: perPage, totalEntries: totalEntries })),
            React.createElement("div", { className: "col-md" },
                React.createElement(Pager, { changePage: changePage, display: true, usrHalfWindowSize: 2, page: currentPage, pages: totalPages })))));
}
export function ListingHeader({ searchStr, handleSearchChange }) {
    return (React.createElement(React.Fragment, null,
        React.createElement(SearchControl, { value: searchStr, handleSearchChange: handleSearchChange }),
        React.createElement("data", { value: searchStr, "data-type": "search", hidden: true })));
}
export function NoResultsFound({ hasEntries, searchStr, resetSearch }) {
    if (hasEntries)
        return null;
    return (React.createElement("div", { className: "alert alert-info text-center", role: "alert" },
        React.createElement("h4", { className: "alert-heading" }, "No Results Found"),
        searchStr && (React.createElement(React.Fragment, null,
            React.createElement("p", null,
                "No Entries match your search for:",
                React.createElement("strong", null,
                    " ",
                    '\u0022',
                    searchStr,
                    '\u0022'),
                "."),
            React.createElement("hr", null),
            React.createElement("button", { className: "btn btn-info mx-2", onClick: resetSearch }, "Reset Search")))));
}
export function SortableHeader({ currentKey, currentDir, sortKey, label, handleSorting }) {
    function renderSortClass() {
        const className = 'sortable';
        if (currentKey !== sortKey)
            return className;
        return `${className} active ${currentDir ? 'up' : 'down'}`;
    }
    function handleSortingWrapper() {
        return handleSorting(sortKey);
    }
    return (React.createElement("th", { className: renderSortClass(), onClick: handleSortingWrapper }, label));
}
export function GlobalSpinner({ display }) {
    if (!display)
        return null;
    return (React.createElement("div", { className: "d-flex justify-content-center flex-grow-1 main-app-loader" },
        React.createElement("div", { className: "spinner-border align-self-center", role: "status" },
            React.createElement("span", { className: "visually-hidden" }, "Loading..."))));
}
export function HumanDate({ usrDate }) {
    if (!usrDate)
        return null;
    const date = new Date(usrDate);
    if (!(date instanceof Date))
        return null;
    if (isNaN(date.getTime()))
        return null;
    const months = [
        'Jan.',
        'Feb.',
        'March',
        'April',
        'May',
        'June',
        'July',
        'Aug.',
        'Sept.',
        'Oct.',
        'Nov.',
        'Dec.'
    ];
    return (React.createElement("time", { dateTime: date.toISOString() },
        months[date.getMonth()],
        " ",
        date.getDate(),
        ", ",
        date.getFullYear()));
}
export function usePaginatedSearchWithSorting(serverUrl) {
    const [firstLoad, setFirstLoad] = useState(true);
    const [entries, setEntries] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalEntries, setTotalEntries] = useState(0);
    const [sortDir, setSortDir] = useState();
    const [sortKey, setSortKey] = useState();
    const [searchStr, setSearchStr] = useState('');
    const [searchStrVisible, setSearchStrVisible] = useState('');
    const [allowedSortKeys, setAllowedSortKeys] = useState([]);
    const [currentPageMax, setCurrentPageMax] = useState(100);
    const [currentPageMin, setCurrentPageMin] = useState(1);
    const parseParams = (params, usrFirstLoad) => {
        params = params || {};
        const usrTotalEntries = params.total_entries || params.totalEntries || 0;
        const usrCurrentPage = params.page || params.current_page || params.currentPage || 1;
        const usrPerPage = params.per_page || params.perPage || 100;
        let usrCurrentPageMax = usrCurrentPage * usrPerPage;
        if (usrCurrentPageMax > usrTotalEntries)
            usrCurrentPageMax = usrTotalEntries;
        const usrCurrentPageMin = ((usrCurrentPage * usrPerPage) - usrPerPage) + 1;
        setFirstLoad(usrFirstLoad);
        setCurrentPage(usrCurrentPage);
        setPerPage(usrPerPage);
        setTotalEntries(usrTotalEntries);
        setCurrentPageMin(usrCurrentPageMin);
        setCurrentPageMax(usrCurrentPageMax);
        setEntries(params.entries || []);
        setTotalPages(params.total_pages || params.totalPages || 1);
        setSortDir(params.sort_dir || params.sortDir || null);
        setSortKey(params.sort_key || params.sortKey || null);
        setSearchStr(params.search_str || params.searchStr || '');
        setAllowedSortKeys(params.allowed_sort_keys || params.allowedSortKeys || []);
    };
    const fetchData = useCallback((searchStr = null, sortKey = null, sortDir = null, page = null) => {
        const params = {
            search_str: searchStr || '',
            sort_key: sortKey || null,
            sort_dir: sortDir || null,
            page: page || 1,
        };
        const paramsMap = new Map([
            ['search_str', 'searchStr'],
            ['sort_key', 'sortKey'],
            ['sort_dir', 'sortDir'],
            ['page', 'page'],
        ]);
        axios.get(serverUrl, { params }).then(resp => {
            parseParams(resp.data);
            let queryStr = (Object.keys(params).reduce((acc, k) => {
                let val = encodeURIComponent(resp.data[paramsMap.get(k)]);
                if (val === undefined || val === 'undefined' || val === 'null' || val === null) {
                    val = '';
                }
                acc.push(`${encodeURIComponent(k)}=${val}`);
                return acc;
            }, [])).join('&');
            queryStr = queryStr ? `?${queryStr}` : '';
            window.history.replaceState({}, document.title, window.location.origin + window.location.pathname + queryStr);
        }).catch(handleRequestErrors);
    }, []);
    const resetSearch = () => {
        fetchData();
    };
    const pushSearchChange = useMemo(() => {
        return debounce((newSearchStr) => {
            fetchData(newSearchStr, sortKey, sortDir, 1);
        }, 200);
    }, [fetchData]);
    const handleSearchChange = (value, instantly) => {
        const newSearch = (value + '');
        const oldSearch = (searchStrVisible + '');
        const newSearchClean = newSearch.trim().toLowerCase();
        const oldSearchClean = (oldSearch || '').trim().toLowerCase();
        setSearchStrVisible(newSearch);
        if (oldSearchClean === newSearchClean)
            return;
        if (instantly) {
            return fetchData(newSearchClean, sortKey, sortDir, 1);
        }
        return pushSearchChange(newSearchClean);
    };
    const handleSorting = (key) => {
        if (!allowedSortKeys.includes(key))
            return;
        const direction = (key === sortKey && sortDir === 'asc') ? 'desc' : 'asc';
        return fetchData(searchStrVisible, key, direction, currentPage);
    };
    const changePage = (page) => {
        if (typeof page !== 'number') {
            page = parseInt(page, 10);
        }
        if (isNaN(page))
            page = 1;
        if (currentPage === page)
            return;
        if (page > totalPages)
            return;
        if (page <= 0)
            return;
        return fetchData(searchStrVisible, sortKey, sortDir, page);
    };
    function toStringOrNull(usrParam) {
        const value = usrParam;
        if (!value)
            return null;
        return value.toString().trim();
    }
    function toNumberOrNull(usrParam) {
        let val = usrParam;
        if (isNumber(val))
            return val;
        val = parseInt(val, 10);
        if (isNaN(val))
            return null;
        return val;
    }
    useEffect(() => {
        const params = {
            search_str: null,
            sort_key: null,
            sort_dir: null,
            page: null,
        };
        window.location.search.replace(/^\?/, '').split('&').forEach(param => {
            let [key, value] = (param + '').split('=', 2);
            key = key.trim().toLowerCase();
            value = decodeURIComponent(value);
            if (Object.hasOwn(params, key) && value !== 'null' && value !== 'undefined') {
                params[key] = value;
            }
        });
        setSearchStrVisible(toStringOrNull(params.search_str));
        fetchData(toStringOrNull(params.search_str), toStringOrNull(params.sort_key), toStringOrNull(params.sort_dir), toNumberOrNull(params.page));
    }, [true]);
    return [
        {
            firstLoad,
            entries,
            currentPage,
            perPage,
            totalPages,
            totalEntries,
            sortDir,
            sortKey,
            searchStr,
            searchStrVisible,
            allowedSortKeys,
            currentPageMax,
            currentPageMin,
        },
        {
            changePage,
            handleSorting,
            handleSearchChange,
            resetSearch
        }
    ];
}
